function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { buildService, api } from '@rd-web-markets/shared/dist/services/service';
import serviceMethods from '@rd-web-markets/shared/dist/services/service_methods';
import { useCallback } from 'react';
const notesApi = buildService('/claims/{id}/notes_and_assumptions', {
  only: ['all', 'create', 'update', 'delete']
});
const notesAndAssumptionsService = {
  useFetchAll: (setLoading, claimId) => {
    const [notes, setNotes, fetchNotes] = serviceMethods.useFetch({
      callback: useCallback(async () => {
        if (!claimId) return [];
        const response = await notesApi.all(claimId);
        const activeChangeSetId = response === null || response === void 0 ? void 0 : response.active_change_set_id;
        const rawNotes = (response === null || response === void 0 ? void 0 : response.notes_and_assumptions) || response || [];
        const processedNotes = rawNotes.map(note => {
          const allReviews = note.notes_and_assumption_reviews || [];
          const activeReviews = allReviews.filter(review => review.change_set_id === activeChangeSetId).sort((a, b) => b.id - a.id);
          const currentReview = activeReviews[0] || null;

          // Check if note has been reviewed before
          const finalizedReviews = allReviews.filter(r => r.status === 'closed' || r.status === 'finalized');

          // Get past reviews (not in current change set)
          const pastReviews = allReviews.filter(r => r.change_set_id !== activeChangeSetId).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          return _objectSpread(_objectSpread({}, note), {}, {
            has_active_cost_review: activeChangeSetId !== null,
            current_review: currentReview,
            active_reviews: activeReviews,
            has_been_reviewed: finalizedReviews.length > 0,
            past_reviews: pastReviews
          });
        });
        return processedNotes;
      }, [claimId]),
      setLoading,
      initialStateValue: []
    });
    return [notes, setNotes, fetchNotes];
  },
  useFetchCategories: (setLoading, claimId) => {
    const [categories, setCategories, fetchCategories] = serviceMethods.useFetch({
      callback: useCallback(async () => {
        if (!claimId) return [];
        const response = await api.get("/claims/".concat(claimId, "/notes_and_assumptions/categories"));
        return (response === null || response === void 0 ? void 0 : response.categories) || [];
      }, [claimId]),
      setLoading,
      initialStateValue: []
    });
    return [categories, setCategories, fetchCategories];
  },
  useCreate: (setLoading, setNotes, notes, claimId, fetchNotes) => {
    const createNote = serviceMethods.useCreate({
      callback: useCallback(async data => {
        if (!claimId) return;
        const response = await notesApi.create(claimId, {
          notes_and_assumption: data
        });

        // Refetch all notes to get properly processed data with correct active_change_set_id
        await fetchNotes();
        return response;
      }, [claimId, fetchNotes]),
      setLoading
    });
    return [createNote];
  },
  useUpdate: (setLoading, setNotes, notes, claimId) => {
    const updateNote = serviceMethods.useUpdate({
      callback: useCallback(async _ref => {
        let {
          id,
          data
        } = _ref;
        if (!claimId) return;
        const response = await notesApi.update(claimId, id, {
          notes_and_assumption: data
        });
        const processedNote = processReviewsForNote(response, null);
        setNotes(notes.map(n => n.id === id ? processedNote : n));
        return processedNote;
      }, [notes, setNotes, claimId]),
      setLoading
    });
    return [updateNote];
  },
  useDelete: (setLoading, setNotes, notes, claimId) => {
    const deleteNote = serviceMethods.useDelete({
      callback: useCallback(async noteId => {
        if (!claimId) return;
        await notesApi.delete(claimId, noteId);
        setNotes(notes.filter(n => n.id !== Number(noteId)));
      }, [notes, setNotes, claimId]),
      setLoading
    });
    return [deleteNote];
  }
};
function processReviewsForNote(note) {
  let active_change_set_id = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  const reviews = note.notes_and_assumption_reviews || [];
  if (reviews.length === 0 || !active_change_set_id) {
    return _objectSpread(_objectSpread({}, note), {}, {
      has_active_cost_review: false,
      current_review: null,
      active_reviews: [],
      past_reviews: []
    });
  }
  const activeReviews = reviews.filter(review => review.change_set_id === active_change_set_id).sort((a, b) => b.id - a.id);
  const pastReviews = reviews.filter(review => review.change_set_id !== active_change_set_id).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  return _objectSpread(_objectSpread({}, note), {}, {
    has_active_cost_review: active_change_set_id !== null && activeReviews.length > 0,
    current_review: activeReviews[0] || null,
    active_reviews: activeReviews,
    past_reviews: pastReviews
  });
}
notesAndAssumptionsService.useCrud = (setLoading, claimId) => {
  const [notes, setNotes, fetchNotes] = notesAndAssumptionsService.useFetchAll(setLoading, claimId);
  const [categories,, fetchCategories] = notesAndAssumptionsService.useFetchCategories(setLoading, claimId);
  const [createNote] = notesAndAssumptionsService.useCreate(setLoading, setNotes, notes, claimId, fetchNotes);
  const [updateNote] = notesAndAssumptionsService.useUpdate(setLoading, setNotes, notes, claimId);
  const [deleteNote] = notesAndAssumptionsService.useDelete(setLoading, setNotes, notes, claimId);
  return {
    notes,
    setNotes,
    fetchNotes,
    categories,
    fetchCategories,
    createNote,
    updateNote,
    deleteNote
  };
};
export default notesAndAssumptionsService;